import {
  PayOverTimeAutopayResult,
  PayOverTimeAutopayResultTypenames,
  PayOverTimeAutopaySuccess,
  SetAutopayForPayOverTimeInput,
} from '@varo/graphql-schema-types';
import {
  buildErrorModel,
  type ErrorModel,
} from 'features/payOverTime/helpers/PayOverTime.helpers';
import { useState } from 'react';
import SetAutopayForPayOverTime from 'services/graphql/mutations/SetAutopayForPayOverTime';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';

export interface UseSetAutopayForPayOverTimeResult {
  setAutopay: (
    input: SetAutopayForPayOverTimeInput,
  ) => Promise<PayOverTimeAutopayResult | null>;
  fetching: boolean;
  data: PayOverTimeAutopaySuccess | null;
  errorModel: ErrorModel | null;
}

export function useSetAutopayForPayOverTime(): UseSetAutopayForPayOverTimeResult {
  const { defaultMessage, defaultTitle } = useDefaultErrorStrings();
  const [{ data, fetching, error }, executeMutation] = useCancellableMutation<{
    setAutopayForPayOverTime: PayOverTimeAutopayResult;
  }>(SetAutopayForPayOverTime);
  const [localErrorModel, setLocalErrorModel] = useState<ErrorModel | null>(
    null,
  );

  const networkErrorModel: ErrorModel | null = error
    ? buildErrorModel(defaultTitle, defaultMessage)
    : null;

  const resultVariant = data?.setAutopayForPayOverTime;

  const successVariant =
    resultVariant?.__typename ===
    PayOverTimeAutopayResultTypenames.PayOverTimeAutopaySuccess
      ? resultVariant
      : null;

  const graphqlErrorVariant: ErrorModel | null =
    resultVariant?.__typename ===
    PayOverTimeAutopayResultTypenames.PayOverTimeAutopayError
      ? buildErrorModel(resultVariant.title, resultVariant.message)
      : null;

  const setAutopay = async (input: SetAutopayForPayOverTimeInput) => {
    setLocalErrorModel(null);

    try {
      const response = await executeMutation({ input });

      return response.data?.setAutopayForPayOverTime ?? null;
    } catch (caught) {
      if (caught instanceof AbortError) {
        return null;
      }

      setLocalErrorModel(buildErrorModel(defaultTitle, defaultMessage));
      return null;
    }
  };

  const errorModel =
    networkErrorModel || graphqlErrorVariant || localErrorModel;

  return {
    setAutopay,
    fetching,
    data: successVariant,
    errorModel,
  };
}

export default useSetAutopayForPayOverTime;
